import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Divider,
  MenuItem,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from "dayjs";
import { formFieldStyles } from "../../styles/formStyles";
import { 
  createInventory, 
  updateInventory, 
  getTypesWithItems, 
  getCategoriesByTypeId, 
  getItemsByCategoryId, 
  getAvailableForTemporary,
  getRoomsAvailableForTemporary
} from "../../services/Inventory";
import { useAuth } from "../../auth/AuthProvider";

const ItemFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state?.item || null;
  const isEdit = !!editData;
  const inputRef = useRef<HTMLInputElement>(null);
  const { getLoginUserInfo } = useAuth();
  const loggedInUser = getLoginUserInfo();
  const WAREHOUSE_ID = loggedInUser?.workspaceId;
  const COMPANY_ID = loggedInUser?.companyId;

  const [rooms, setRooms] = useState<any[]>([]);
  const [buildings, setBuildings] = useState<any[]>([]);
  const [types, setTypes] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [purchaseDateValue, setPurchaseDateValue] = useState<Dayjs | null>(null);
  const [expiryDateValue, setExpiryDateValue] = useState<Dayjs | null>(null);
  const [loading,setLoading] = useState(false); 
  const [dateErrors, setDateErrors] = useState<{ purchaseDate?: string; expireDate?: string }>({});
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState({
    typeId: "",
    categoryId: "",
    itemId: "",
    supplier: "",
    itemModel: "",
    length: "",
    width: "",
    height: "",
    weight: 0,
    costPrice: 0,
    sellingPrice: "",
    purchaseDate: "",
    expireDate: "",
    buildingId: "",
    roomId: "",
  });

  const loadBuildings = async (length: string, width: string, height: string) => {
    if (!length || !width || !height) return;
    try {
      const data = await getAvailableForTemporary({
        length: Number(length),
        width: Number(width),
        height: Number(height)
      });
      setBuildings(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load buildings:", e);
      setBuildings([]);
    }
  };

  const loadRooms = async (buildingId: string) => {
    if (!buildingId || !formData.length || !formData.width || !formData.height) {
      setRooms([]);
      return;
    }
    try {
      const data = await getRoomsAvailableForTemporary({
        length: Number(formData.length),
        width: Number(formData.width),
        height: Number(formData.height),
        buildingId: Number(buildingId)
      });
      setRooms(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load rooms:", e);
      setRooms([]);
    }
  };

  const loadTypes = async () => {
    try {
      const data = await getTypesWithItems();
      setTypes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load types:", error);
      setTypes([]);
    }
  };

  const loadCategories = async (typeId: string) => {
    if (!typeId) return;
    try {
      const data = await getCategoriesByTypeId(Number(typeId));
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load categories:", error);
      setCategories([]);
    }
  };

  const loadProducts = async (categoryId: string) => {
    if (!categoryId) return;
    try {
      const data = await getItemsByCategoryId(Number(categoryId));
      setProducts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load products:", error);
      setProducts([]);
    }
  };

  useEffect(() => {
    const loadEditData = async () => {
      await loadTypes();
      if (editData) {
        // Set image preview from existing data
        if (editData.imageUrl) {
          setImagePreview(editData.imageUrl);
        }
        
        setFormData((prev) => ({
          ...prev,
          // Map API response fields to form fields
          typeId: String(editData.itemTypeId || ""),
          categoryId: String(editData.categoryId || ""),
          itemId: String(editData.itemId || ""),
          supplier: editData.supplierName || "",
          itemModel: editData.model || "",
          length: String(editData.lengthCm || ""),
          width: String(editData.widthCm || ""),
          height: String(editData.heightCm || ""),
          weight: editData.itemWeight || 0,
          costPrice: editData.itemCost || 0,
          sellingPrice: String(editData.sellingPrice || ""),
          purchaseDate: editData.purchaseDate ? editData.purchaseDate.split('T')[0] : "",
          expireDate: editData.expiryDate ? editData.expiryDate.split('T')[0] : "",
          buildingId: String(editData.buildingId || ""),
          roomId: String(editData.roomId || "")
        }));
        
        // Set date picker values
        if (editData.purchaseDate) {
          setPurchaseDateValue(dayjs(editData.purchaseDate));
        }
        if (editData.expiryDate) {
          setExpiryDateValue(dayjs(editData.expiryDate));
        }
        if (editData.itemTypeId) await loadCategories(String(editData.itemTypeId));
        if (editData.categoryId) await loadProducts(String(editData.categoryId));
      }
    };
    loadEditData();
  }, [editData]);

  useEffect(() => {
    const { length, width, height } = formData;
    if (length && width && height) {
      loadBuildings(length, width, height);
    }
  }, [formData.length, formData.width, formData.height]);

  useEffect(() => {
    if (formData.buildingId) {
      loadRooms(formData.buildingId);
    }
  }, [formData.buildingId, formData.length, formData.width, formData.height]);

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!formData.typeId) errors.typeId = "Type is required";
    if (!formData.categoryId) errors.categoryId = "Category is required";
    if (!formData.itemId) errors.itemId = "Item name is required";
    if (!formData.supplier.trim()) errors.supplier = "Supplier is required";
    if (!formData.itemModel.trim()) errors.itemModel = "Item model is required";
    if (!formData.length) errors.length = "Length is required";
    else if (Number(formData.length) <= 0) errors.length = "Length must be greater than 0";
    if (!formData.width) errors.width = "Width is required";
    else if (Number(formData.width) <= 0) errors.width = "Width must be greater than 0";
    if (!formData.height) errors.height = "Height is required";
    else if (Number(formData.height) <= 0) errors.height = "Height must be greater than 0";
    if (!formData.purchaseDate) errors.purchaseDate = "Purchase date is required";
    if (!formData.expireDate) errors.expireDate = "Expiry date is required";
    if (!formData.sellingPrice) errors.sellingPrice = "Selling price is required";
    if (!isEdit) {
      if (!formData.buildingId) errors.buildingId = "Transit building is required";
      if (!formData.roomId) errors.roomId = "Transit room is required";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateDates = (purchaseDate: string, expiryDate: string) => {
    const errors: { purchaseDate?: string; expireDate?: string } = {};
    
    if (purchaseDate && expiryDate) {
      const purchase = dayjs(purchaseDate);
      const expiry = dayjs(expiryDate);
      
      if (purchase.isAfter(expiry) || purchase.isSame(expiry)) {
        errors.purchaseDate = "Purchase date must be before expiry date";
        errors.expireDate = "Expiry date must be after purchase date";
      }
    }
    
    setDateErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePurchaseDateChange = (date: Dayjs | null) => {
    setPurchaseDateValue(date);
    const dateString = date ? dayjs(date).format("YYYY-MM-DD") : "";
    setFormData((prev) => ({ ...prev, purchaseDate: dateString }));
    
    // Validate dates when purchase date changes
    if (dateString && formData.expireDate) {
      validateDates(dateString, formData.expireDate);
    } else {
      setDateErrors({});
    }
  };

  const handleExpiryDateChange = (date: Dayjs | null) => {
    setExpiryDateValue(date);
    const dateString = date ? dayjs(date).format("YYYY-MM-DD") : "";
    setFormData((prev) => ({ ...prev, expireDate: dateString }));
    
    // Validate dates when expiry date changes
    if (formData.purchaseDate && dateString) {
      validateDates(formData.purchaseDate, dateString);
    } else {
      setDateErrors({});
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    const ALLOWED_TYPES = ["image/jpeg", "image/png"];

    if (!ALLOWED_TYPES.includes(file.type)) {
      alert("Only JPG & PNG files are allowed");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      alert("Max file size is 5 MB");
      return;
    }

    setSelectedImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const clearError = (field: string) => {
    setFormErrors((prev) => { const e = { ...prev }; delete e[field]; return e; });
  };

  const handleTypeChange = (value: string) => {
    clearError("typeId");
    setFormData((prev) => ({
      ...prev,
      typeId: value,
      categoryId: "",
      itemId: "",
      weight: 0,
      costPrice: 0,
      sellingPrice: "",
    }));
    setCategories([]);
    setProducts([]);
    loadCategories(value);
  };

  const handleCategoryChange = (value: string) => {
    clearError("categoryId");
    setFormData((prev) => ({
      ...prev,
      categoryId: value,
      itemId: "",
      weight: 0,
      costPrice: 0,
      sellingPrice: "",
    }));
    setProducts([]);
    loadProducts(value);
  };

  const handleProductChange = (value: string) => {
    clearError("itemId");
    const selected = products.find((p) => p.id === Number(value));
    setFormData((prev) => ({
      ...prev,
      itemId: value,
      weight: selected?.weight || 0,
      costPrice: selected?.cost || 0,
      sellingPrice: String(selected?.sellingPrice || 0),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!COMPANY_ID || !WAREHOUSE_ID) {
      setSnackbar({ open: true, message: "User information not available", severity: "error" });
      return;
    }

    if (!validateForm()) {
      setSnackbar({ open: true, message: "Please fill in all required fields", severity: "error" });
      return;
    }

    // Validate dates before submission
    if (formData.purchaseDate && formData.expireDate) {
      if (!validateDates(formData.purchaseDate, formData.expireDate)) {
        setSnackbar({ open: true, message: "Please fix date validation errors", severity: "error" });
        return;
      }
    }
    
    // Map form data to inventory DTO structure
    const inventoryData = {
      companyId: COMPANY_ID,
      warehouseId: WAREHOUSE_ID,
      itemTypeId: Number(formData.typeId) || 0,
      categoryId: Number(formData.categoryId) || 0,
      itemId: Number(formData.itemId) || 0,
      supplierName: formData.supplier || "",
      model: formData.itemModel || "",
      lengthCm: Number(formData.length) || 0,
      widthCm: Number(formData.width) || 0,
      heightCm: Number(formData.height) || 0,
      purchaseDate: formData.purchaseDate
        ? `${formData.purchaseDate}T00:00:00`
        : "",
      expiryDate: formData.expireDate
        ? `${formData.expireDate}T00:00:00`
        : "",
      sellingPrice: Number(formData.sellingPrice) || 0,
      itemCost: formData.costPrice || 0,
      itemWeight: formData.weight || 0,
      // Only include building/room for create mode
      ...(isEdit ? {} : {
        buildingId: Number(formData.buildingId) || 0,
        roomId: Number(formData.roomId) || 0,
      }),
    };
    setLoading(true);
    try {
      if (isEdit) {
     const response =   await updateInventory(editData.id, inventoryData, selectedImage || undefined);
     console.log(" add item response",response);
     if(response && response.success){
        setSnackbar({ open: true, message: "Item updated successfully", severity: "success" });
        setTimeout(() => navigate("/inventory/items"), 1500);
      }else{
        setSnackbar({ open: true, message: "Updating Item Failed", severity: "error" });
      }
      } else {
      const response =  await createInventory(inventoryData, selectedImage || undefined);
      console.log(" add item response",response);
      if(response && response.success){
        setSnackbar({ open: true, message: "Item added successfully", severity: "success" });
        setTimeout(() => navigate("/inventory/items"), 1500);
      } else{
        setSnackbar({ open: true, message: "Adding Item failed", severity: "error" });
      }
    }
    } catch (error) {
      console.error("Submit failed:", error);
      setSnackbar({ open: true, message: isEdit ? "Failed to update item" : "Failed to add item", severity: "error" });
    }finally{
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p:1, height: "calc(100vh - 3.5rem)", display: "flex", flexDirection: "column" }}>
      <Button
        onClick={() => navigate("/inventory/items")}
        sx={{ border: "none", textTransform: "none", color: "text.primary", mb:1, alignSelf: "flex-start" }}
        startIcon={<ArrowBackIosNewOutlinedIcon fontSize="small" />}
      >
        Back
      </Button>

      <Box sx={{ backgroundColor: "background.paper", p:2, borderRadius: 2, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <Typography variant="body2" fontWeight={600} mb={0.5} fontSize="20px">
          {isEdit ? "Edit Inventory Item" : "Add Inventory Item"}
        </Typography>
        <Typography variant="body2" color="grey.500" mb={3} fontSize="12px" fontWeight={400}>
          {isEdit ? "Edit inventory item details here. Click Save when you're done." : "Add inventory item details here. Click add when you're done."}
        </Typography>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
          <Box sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#CBD5E1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#94A3B8",
            },
            "&::-webkit-scrollbar-button": {
              display: "none",
            },
          }}>
            <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Typography sx={{ mb: 0.5, fontSize: "14px", fontWeight: 500 }}>
                Product Image <span style={{ color: "red" }}>*</span>
              </Typography>
              
              <Box
                onClick={() => inputRef.current?.click()}
                sx={{
                  height: "60px",
                  borderRadius: "6px",
                  border: "1px solid rgba(74,85,101,0.25)",
                  backgroundColor: "#F9FAFB",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                  cursor: "pointer",
                  transition: "0.2s",
                  "&:hover": {
                    borderColor: "#000",
                    backgroundColor: "#F3F4F6",
                  },
                }}
              >
                <AddPhotoAlternateOutlinedIcon fontSize="small" sx={{ color: "grey.primary" }} />
              </Box>

              <input
                ref={inputRef}
                type="file"
                accept="image/png, image/jpeg"
                hidden
                onChange={handleImageChange}
              />

              <Typography fontSize={11} color="#6B7280" mt={0.5}>
                Max file size: 5 MB · JPG & PNG only
              </Typography>

              {imagePreview && (
                <Box
                  mt={1.5}
                  position="relative"
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "6px",
                    border: "1px solid #E5E7EB",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    component="img"
                    src={imagePreview}
                    alt="preview"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />

                  <IconButton
                    onClick={handleRemoveImage}
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      color: "#EF4444",
                      padding: "2px",
                      "&:hover": {
                        color: "#DC2626",
                      },
                    }}
                  >
                    <CloseOutlinedIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}

              <Box mt={3} display="flex" alignItems="center" justifyContent="space-between">
                <Typography color="grey.500" fontSize="14px" fontWeight={500}>Item Cost :</Typography>
                <Typography fontSize="14px" fontWeight={600}>₹ {formData.costPrice || 0}</Typography>
              </Box>

              <Box mt={2} display="flex" alignItems="center" justifyContent="space-between">
                <Typography color="grey.500" fontSize="14px" fontWeight={500}>Item weight :</Typography>
                <Typography fontSize="14px" fontWeight={600}>{formData.weight || 0} KG</Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={9}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography sx={{ mb: 0.5, fontSize: "14px", fontWeight: 500 }}>
                    Type <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={formData.typeId}
                    onChange={(e) => handleTypeChange(e.target.value)}
                    SelectProps={{ displayEmpty: true }}
                    error={!!formErrors.typeId}
                    helperText={formErrors.typeId}
                    sx={formFieldStyles}
                  >
                    <MenuItem value="" disabled>Select Type</MenuItem>
                    {Array.isArray(types) && types.map((t) => (
                      <MenuItem key={t.id} value={t.id}>{t.typeName}</MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography sx={{ mb: 0.5, fontSize: "14px", fontWeight: 500 }}>
                    Category <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={formData.categoryId}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    disabled={!formData.typeId}
                    SelectProps={{ displayEmpty: true }}
                    error={!!formErrors.categoryId}
                    helperText={formErrors.categoryId}
                    sx={formFieldStyles}
                  >
                    <MenuItem value="" disabled>Select Category</MenuItem>
                    {Array.isArray(categories) && categories.map((c) => (
                      <MenuItem key={c.id} value={c.id}>{c.categoryName}</MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography sx={{ mb: 0.5, fontSize: "14px", fontWeight: 500 }}>
                    Item name <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={formData.itemId}
                    onChange={(e) => handleProductChange(e.target.value)}
                    disabled={!formData.categoryId}
                    SelectProps={{ displayEmpty: true }}
                    error={!!formErrors.itemId}
                    helperText={formErrors.itemId}
                    sx={formFieldStyles}
                  >
                    <MenuItem value="" disabled>Select Item</MenuItem>
                    {Array.isArray(products) && products.map((p) => (
                      <MenuItem key={p.id} value={p.id}>{p.itemName}</MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography sx={{ mb: 0.5, fontSize: "14px", fontWeight: 500 }}>
                    Item Supplier <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Vijay kumar"
                    value={formData.supplier}
                    onChange={(e) => setFormData((prev) => ({ ...prev, supplier: e.target.value }))}
                    error={!!formErrors.supplier}
                    helperText={formErrors.supplier}
                    sx={formFieldStyles}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography sx={{ mb: 0.5, fontSize: "14px", fontWeight: 500 }}>
                    Item model <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="8402pe88h828"
                    value={formData.itemModel}
                    onChange={(e) => setFormData((prev) => ({ ...prev, itemModel: e.target.value }))}
                    error={!!formErrors.itemModel}
                    helperText={formErrors.itemModel}
                    sx={formFieldStyles}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography sx={{ mb: 0.5, fontSize: "14px", fontWeight: 500 }}>
                    Dimension (L x W x H cm) <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <Box display="flex" gap={1}>
                    <TextField
                      type="number"
                      size="small"
                      placeholder="20"
                      value={formData.length}
                      onChange={(e) => setFormData((prev) => ({ ...prev, length: e.target.value }))}
                      error={!!formErrors.length}
                      sx={{ ...formFieldStyles, flex: 1 }}
                    />
                    <TextField
                      type="number"
                      size="small"
                      placeholder="15"
                      value={formData.width}
                      onChange={(e) => setFormData((prev) => ({ ...prev, width: e.target.value }))}
                      error={!!formErrors.width}
                      sx={{ ...formFieldStyles, flex: 1 }}
                    />
                    <TextField
                      type="number"
                      size="small"
                      placeholder="10"
                      value={formData.height}
                      onChange={(e) => setFormData((prev) => ({ ...prev, height: e.target.value }))}
                      error={!!formErrors.height}
                      sx={{ ...formFieldStyles, flex: 1 }}
                    />
                  </Box>
                  {(formErrors.length || formErrors.width || formErrors.height) && (
                    <Typography fontSize={12} color="error" mt={0.5}>
                      {formErrors.length || formErrors.width || formErrors.height}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography sx={{ mb: 0.5, fontSize: "14px", fontWeight: 500 }}>
                    Purchase Date <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={purchaseDateValue}
                      onChange={handlePurchaseDateChange}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                          error: !!dateErrors.purchaseDate || !!formErrors.purchaseDate,
                          helperText: dateErrors.purchaseDate || formErrors.purchaseDate,
                          sx: formFieldStyles
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography sx={{ mb: 0.5, fontSize: "14px", fontWeight: 500 }}>
                    Expiry Date <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={expiryDateValue}
                      onChange={handleExpiryDateChange}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                          error: !!dateErrors.expireDate || !!formErrors.expireDate,
                          helperText: dateErrors.expireDate || formErrors.expireDate,
                          sx: formFieldStyles
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography sx={{ mb: 0.5, fontSize: "14px", fontWeight: 500 }}>
                    Item Selling Price <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <TextField
                    type="number"
                    fullWidth
                    size="small"
                    placeholder="₹ 1050000"
                    value={formData.sellingPrice}
                    InputProps={{ readOnly: true }}
                    error={!!formErrors.sellingPrice}
                    helperText={formErrors.sellingPrice}
                    sx={{
                      ...formFieldStyles,
                      '& .MuiInputBase-input': {
                        backgroundColor: '#f5f5f5',
                        cursor: 'default'
                      }
                    }}
                  />
                </Grid>

                {/* Transit Building - Only show in create mode */}
                {!isEdit && (
                  <Grid item xs={12} md={4}>
                    <Typography sx={{ mb: 0.5, fontSize: "14px", fontWeight: 500 }}>
                      Transit Building <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={formData.buildingId}
                      onChange={(e) => setFormData((prev) => ({ ...prev, buildingId: e.target.value }))}
                      SelectProps={{ displayEmpty: true }}
                      error={!!formErrors.buildingId}
                      helperText={formErrors.buildingId}
                      sx={formFieldStyles}
                    >
                      <MenuItem value="" disabled>Select Building</MenuItem>
                      {Array.isArray(buildings) && buildings.map((b) => (
                        <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}

                {/* Transit Room - Only show in create mode */}
                {!isEdit && (
                  <Grid item xs={12} md={4}>
                    <Typography sx={{ mb: 0.5, fontSize: "14px", fontWeight: 500 }}>
                      Transit Room <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={formData.roomId}
                      onChange={(e) => setFormData((prev) => ({ ...prev, roomId: e.target.value }))}
                      SelectProps={{ displayEmpty: true }}
                      error={!!formErrors.roomId}
                      helperText={formErrors.roomId}
                      sx={formFieldStyles}
                    >
                      <MenuItem value="" disabled>Select Room</MenuItem>
                      {Array.isArray(rooms) && rooms.map((r) => (
                        <MenuItem key={r.id} value={r.id}>{r.roomName || `Room ${r.id}`}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box display="flex" justifyContent="space-between" sx={{ flexShrink: 0 }}>
            <Button
              variant="outlined"
              onClick={() => navigate("/inventory/items")}
              sx={{
                borderRadius: "6px",
                border: "1px solid",
                borderColor: "primary.main",
                boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset",
                padding: "8px 16px",
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <Button
            disabled={loading}
              type="submit"
              variant="contained"
              sx={{
                borderRadius: "6px",
                border: "1px solid",
                borderColor: "primary.main",
                boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset",
                padding: "8px 40px",
                textTransform: "none",
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : isEdit ? "Update" : "Add "}
            </Button>
          </Box>
        </form>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ 
            width: "100%",
            backgroundColor: `${snackbar.severity === "success" ? "success.main" : "error.main"} !important`,
            color: "white",
            "& .MuiAlert-icon": { color: "white" },
            "& .MuiAlert-action": { color: "white" }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
export default ItemFormPage;